import { useState, useEffect, useCallback } from 'react'
import { supabase } from '@/lib/supabase'
import { useAuth } from '@/contexts/AuthContext'

export interface UpcomingLesson {
  id: string
  relationshipId: string
  studentId: string
  studentName: string | null
  scheduledAt: string
  plannedEndAt: string | null
  notes: string | null
}

export interface IncomingRequest {
  id: string
  kind: 'new_booking' | 'reschedule'
  relationshipId: string
  lessonId: string | null
  studentId: string
  studentName: string | null
  requestedStart: string
  requestedEnd: string
  notes: string | null
}

interface ActionResult {
  ok: boolean
  code?: string
}

/**
 * The instructor's own calendar (migration 094): their upcoming confirmed lessons
 * (status='scheduled', finished_at still null -- a lesson that has already been started/
 * completed via the live Start/End flow drops off this list on its own, since finished_at
 * becomes non-null) and incoming pending requests from students, with the actions to book/
 * cancel/reschedule directly (immediate) and confirm/decline a pending request.
 */
export function useInstructorCalendar() {
  const { user } = useAuth()
  const [lessons, setLessons] = useState<UpcomingLesson[]>([])
  const [requests, setRequests] = useState<IncomingRequest[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [saving, setSaving] = useState(false)

  const fetchAll = useCallback(async () => {
    if (!user) { setLoading(false); return }
    setLoading(true)
    setError(null)
    try {
      const [lessonsRes, requestsRes] = await Promise.all([
        supabase.from('driving_lessons')
          .select('id, relationship_id, student_id, scheduled_at, planned_end_at, notes')
          .eq('instructor_id', user.id)
          .eq('status', 'scheduled')
          .is('finished_at', null)
          .order('scheduled_at', { ascending: true }),
        supabase.from('lesson_booking_requests')
          .select('id, kind, relationship_id, lesson_id, student_id, requested_start, requested_end, notes')
          .eq('instructor_id', user.id)
          .eq('status', 'pending')
          .order('requested_start', { ascending: true }),
      ])

      if (lessonsRes.error || requestsRes.error) { setError('Could not load your calendar.'); return }

      const studentIds = [...new Set([
        ...(lessonsRes.data ?? []).map(l => l.student_id),
        ...(requestsRes.data ?? []).map(r => r.student_id),
      ])]
      const { data: profiles } = studentIds.length
        ? await supabase.from('profiles').select('id, full_name').in('id', studentIds)
        : { data: [] }
      const nameById = new Map((profiles ?? []).map(p => [p.id, p.full_name]))

      setLessons((lessonsRes.data ?? []).map(l => ({
        id: l.id, relationshipId: l.relationship_id, studentId: l.student_id,
        studentName: nameById.get(l.student_id) ?? null,
        scheduledAt: l.scheduled_at, plannedEndAt: l.planned_end_at, notes: l.notes,
      })))
      setRequests((requestsRes.data ?? []).map(r => ({
        id: r.id, kind: r.kind as 'new_booking' | 'reschedule', relationshipId: r.relationship_id,
        lessonId: r.lesson_id, studentId: r.student_id,
        studentName: nameById.get(r.student_id) ?? null,
        requestedStart: r.requested_start, requestedEnd: r.requested_end, notes: r.notes,
      })))
    } finally {
      setLoading(false)
    }
  }, [user])

  useEffect(() => { fetchAll() }, [fetchAll])

  const bookLesson = useCallback(async (fields: {
    relationshipId: string; startAt: string; endAt: string; notes?: string | null
  }): Promise<ActionResult & { autoConfirmed?: boolean }> => {
    if (saving) return { ok: false, code: 'busy' }
    setSaving(true)
    try {
      const { data, error: fnError } = await supabase.functions.invoke('book-lesson', {
        body: { relationshipId: fields.relationshipId, startAt: fields.startAt, endAt: fields.endAt, notes: fields.notes ?? null },
      })
      if (fnError) return { ok: false, code: 'internal_error' }
      const result = data as ActionResult & { auto_confirmed?: boolean }
      if (result.ok) await fetchAll()
      return { ok: result.ok, code: result.code, autoConfirmed: result.auto_confirmed }
    } finally {
      setSaving(false)
    }
  }, [saving, fetchAll])

  const cancelLesson = useCallback(async (lessonId: string, reason?: string | null): Promise<ActionResult> => {
    if (saving) return { ok: false, code: 'busy' }
    setSaving(true)
    try {
      const { data, error: fnError } = await supabase.functions.invoke('cancel-lesson', {
        body: { lessonId, reason: reason ?? null },
      })
      if (fnError) return { ok: false, code: 'internal_error' }
      const result = data as ActionResult
      if (result.ok) await fetchAll()
      return result
    } finally {
      setSaving(false)
    }
  }, [saving, fetchAll])

  const rescheduleLesson = useCallback(async (lessonId: string, newStartAt: string, newEndAt: string): Promise<ActionResult> => {
    if (saving) return { ok: false, code: 'busy' }
    setSaving(true)
    try {
      const { data, error: fnError } = await supabase.functions.invoke('reschedule-lesson', {
        body: { lessonId, newStartAt, newEndAt },
      })
      if (fnError) return { ok: false, code: 'internal_error' }
      const result = data as ActionResult
      if (result.ok) await fetchAll()
      return result
    } finally {
      setSaving(false)
    }
  }, [saving, fetchAll])

  const respondToRequest = useCallback(async (requestId: string, approve: boolean): Promise<ActionResult> => {
    if (saving) return { ok: false, code: 'busy' }
    setSaving(true)
    try {
      const { data, error: fnError } = await supabase.functions.invoke('respond-booking-request', {
        body: { requestId, approve },
      })
      if (fnError) return { ok: false, code: 'internal_error' }
      const result = data as ActionResult
      if (result.ok) await fetchAll()
      return result
    } finally {
      setSaving(false)
    }
  }, [saving, fetchAll])

  return {
    lessons, requests, loading, error, saving,
    bookLesson, cancelLesson, rescheduleLesson, respondToRequest, refresh: fetchAll,
  }
}
